import { useCallback, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router";
import { ReportAccessType } from "@entrilia/shared/api/reportAccessTypes.ts";
import ReportAuthorizedBox from "@entrilia/shared/components/ReportAuthorizedBox";
import { checkAccess } from "@entrilia/shared/utilities/accessHelper.ts";
import type { ReportAuthorizationInfo, ReportInfo } from "../../../../../../../../api/types/svTypes.ts";
import { ReportType } from "../../../../../../../../api/types/svTypes.ts";
import useClientContext from "../../../../../../../../context/useClientContext.ts";
import useUserContext from "../../../../../../../../context/useUserContext.ts";
import { useEnhancedBiApiClientProvider } from "../../../../contexts/ApiClientProviderContext.ts";
import { useBiClientContext } from "../../../../contexts/ClientContext";
import {
  selectCurrentReport,
  selectCurrentReportAuthorizationInfo,
  selectReportEditMode,
} from "../../../../store/selectors/currentReportSelectors";
import { createCurrentReportAction } from "../../../../store/utils/storeUtils";
import ShareReport from "../../../viewer/common/sharing/ShareReport.tsx";
import type { CurrentUser, ReportCompany } from "../../../viewer/common/sharing/ShareReport.types.ts";
import ShareButton from "./ShareButton.tsx";

export default function ShareReportButton({ disabled = false }: { disabled?: boolean }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id, name, email } = useUserContext();
  const { getReportsUrl } = useEnhancedBiApiClientProvider();
  const { clientInfo } = useBiClientContext();
  const { clientCode } = useClientContext();
  const report = useSelector(selectCurrentReport);
  const reportAuthorization = useSelector(selectCurrentReportAuthorizationInfo);
  const [showSharedDialog, setShowSharedDialog] = useState(false);
  const isEditMode = useSelector(selectReportEditMode);

  const currentUser = useMemo((): CurrentUser => ({ id, name, email }), [id, name, email]);
  const isReportPrivate = useMemo(() => reportAuthorization?.private === true, [reportAuthorization]);

  const reportCompany = useMemo((): ReportCompany | undefined => {
    return {
      clientCode: clientCode,
      name: clientInfo.title,
      logoMarkUrl: clientInfo.branding.logoMarkUrl,
    };
  }, [clientCode, clientInfo]);

  const reportInfo = useMemo((): ReportInfo | undefined => {
    if (report === undefined || reportAuthorization === undefined) {
      return undefined;
    }
    return {
      clientCode: report.clientCode,
      reportId: report.reportId,
      name: report.name,
      reportType: report.reportType || ReportType.Tabular,
      groupId: report.groupId ?? "",
      updateAt: "",
      modifiedBy: "",
      createdBy: "",
      authorization: reportAuthorization,
      inheritFromParent: report.inheritFromParent ?? false,
      reportUsage: report.reportUsage,
    };
  }, [report, reportAuthorization]);

  const handleClosed = useCallback(() => {
    setShowSharedDialog(false);
  }, []);

  const handleAuthorizationUpdated = useCallback(
    (authorization: ReportAuthorizationInfo, inheritFromParent: boolean) => {
      dispatch(createCurrentReportAction("setAuthorizationInfo", authorization));
      dispatch(createCurrentReportAction("updateReport", { inheritFromParent }));
      if (
        authorization.access === ReportAccessType.NoAccess ||
        (isEditMode && !checkAccess(authorization.access, ReportAccessType.Write))
      ) {
        navigate(getReportsUrl(clientCode));
      }
    },
    // eslint-disable-next-line @eslint-react/exhaustive-deps
    [isEditMode, clientCode]
  );

  if (reportInfo === undefined) {
    return null;
  }

  if (reportInfo.reportId === undefined) {
    return null;
  }

  return (
    <>
      <ReportAuthorizedBox
        permissions={["ShareReports"]}
        access={reportInfo.authorization.access}
        requiredAccess={ReportAccessType.FullAccess}
      >
        <ShareButton isPrivate={isReportPrivate} disabled={disabled} onClick={() => setShowSharedDialog(true)} />
        {showSharedDialog && (
          <ShareReport
            report={reportInfo}
            currentUser={currentUser}
            reportCompany={reportCompany!}
            onClose={handleClosed}
            onAuthorizationUpdated={handleAuthorizationUpdated}
          />
        )}
      </ReportAuthorizedBox>
    </>
  );
}
